import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, CalendarClock, CheckCircle2, Clock, Inbox, Loader2,
  MapPin, Stethoscope, X, XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BUCKETS = [
  { v: 'offered', label: 'Open offers', tone: 'bg-blue-100 text-blue-700', icon: Clock },
  { v: 'accepted', label: 'Accepted', tone: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  { v: 'declined', label: 'Declined', tone: 'bg-red-100 text-red-700', icon: XCircle },
  { v: 'expired_no_response', label: 'Expired', tone: 'bg-slate-100 text-slate-600', icon: Clock },
];

const URGENCY_TONE = {
  urgent: 'bg-red-100 text-red-800 border-red-200',
  priority: 'bg-amber-100 text-amber-800 border-amber-200',
  routine: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const useCountdown = (deadlineIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadlineIso) return null;
  const diffMin = Math.round((new Date(deadlineIso).getTime() - now) / 60000);
  if (diffMin <= 0) return { label: 'Expired', expired: true };
  if (diffMin < 60) return { label: `${diffMin}m left`, expired: false };
  return { label: `${Math.round(diffMin / 60)}h left`, expired: false };
};

const OfferCard = ({ p, onAccept, onDecline, onCommitFirstVisit, busy }) => {
  const cd = useCountdown(p.expires_at);
  return (
    <div className="cn-card rounded-2xl p-5 space-y-3" data-testid={`spine-offer-card-${p.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border ${URGENCY_TONE[p.urgency] || URGENCY_TONE.routine}`} data-testid={`spine-offer-urgency-${p.id}`}>{p.urgency}</span>
          {cd && (
            <span className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${cd.expired ? 'bg-red-100 text-red-800' : 'bg-blue-50 text-blue-700'}`} data-testid={`spine-offer-countdown-${p.id}`}>
              <Clock className="w-3 h-3" /> {cd.label}
            </span>
          )}
        </div>
        <span className="text-xs text-cn-text-muted">Offered {new Date(p.offered_at).toLocaleString()}</span>
      </div>

      <div>
        <div className="text-sm text-cn-text-muted">Patient (encrypted)</div>
        <div className="font-semibold text-cn-text">#{(p.patient_id || '').slice(0, 8)}</div>
      </div>

      {p.first_visit_at && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid={`spine-offer-first-visit-${p.id}`}>
          <CalendarClock className="w-3.5 h-3.5" />
          First visit committed: <b>{new Date(p.first_visit_at).toLocaleString()}</b>
        </div>
      )}

      {p.status === 'offered' && (
        <div className="flex gap-2 pt-2 border-t border-cn-border">
          <button
            disabled={busy === p.id}
            onClick={() => onAccept(p)}
            className="cn-btn cn-btn-primary flex-1 justify-center"
            data-testid={`spine-offer-accept-${p.id}`}
          >
            {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Accept</>}
          </button>
          <button
            disabled={busy === p.id}
            onClick={() => onDecline(p)}
            className="cn-btn flex-1 justify-center !bg-white !text-red-700 !border !border-red-200 hover:!bg-red-50"
            data-testid={`spine-offer-decline-${p.id}`}
          >
            <XCircle className="w-4 h-4" /> Decline
          </button>
        </div>
      )}
      {p.status === 'accepted' && !p.first_visit_at && (
        <div className="pt-2 border-t border-cn-border">
          <button
            disabled={busy === p.id}
            onClick={() => onCommitFirstVisit(p)}
            className="cn-btn cn-btn-primary w-full justify-center"
            data-testid={`spine-offer-commit-first-visit-${p.id}`}
          >
            <CalendarClock className="w-4 h-4" /> Schedule first visit
          </button>
        </div>
      )}
      {p.status === 'declined' && (
        <div className="text-xs text-cn-text-muted pt-2 border-t border-cn-border">
          {p.decline_reason && <span>Reason: <i>&ldquo;{p.decline_reason}&rdquo;</i></span>}
        </div>
      )}
    </div>
  );
};

const DeclineModal = ({ open, onClose, onConfirm, placement, busy }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="spine-decline-modal">
        <DialogHeader>
          <DialogTitle>Decline this offer?</DialogTitle>
          <DialogDescription>
            Optional reason helps the orchestrator route to the next clinician faster.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Schedule full this week, out of region…" rows={3} className="rounded-xl" data-testid="spine-decline-reason" />
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="cn-btn flex-1 justify-center" data-testid="spine-decline-cancel">Cancel</button>
          <button
            disabled={busy}
            onClick={() => onConfirm(placement, reason)}
            className="cn-btn cn-btn-primary flex-1 justify-center"
            data-testid="spine-decline-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Decline offer'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FirstVisitModal = ({ open, onClose, onConfirm, placement, busy }) => {
  const minDt = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16);
  const [at, setAt] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => { if (open) { setAt(''); setNotes(''); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="spine-first-visit-modal">
        <DialogHeader>
          <DialogTitle>Schedule first visit</DialogTitle>
          <DialogDescription>
            Commit a first-visit window — this lands back on the discharge planner&apos;s dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>First visit date &amp; time</Label>
            <Input
              type="datetime-local"
              min={minDt}
              value={at}
              onChange={(e) => setAt(e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
              data-testid="spine-first-visit-at"
              required
            />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Will call to confirm 24h before…" className="rounded-xl" data-testid="spine-first-visit-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="cn-btn flex-1 justify-center" data-testid="spine-first-visit-cancel">Cancel</button>
            <button
              disabled={busy || !at}
              onClick={() => onConfirm(placement, at, notes)}
              className="cn-btn cn-btn-primary flex-1 justify-center"
              data-testid="spine-first-visit-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Commit visit'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SpineClinicianOffers() {
  const { getAuthHeader, user } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState('offered');
  const [busy, setBusy] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [firstVisitTarget, setFirstVisitTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/spine/placements?status=${bucket}&limit=100`, getAuthHeader());
      setPlacements(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        toast.error(`Entitlement disabled: ${detail?.entitlement_required || 'placement_orchestration'}`);
      } else {
        toast.error('Failed to load placements');
      }
    } finally { setLoading(false); }
  }, [getAuthHeader, bucket]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (p) => {
    setBusy(p.id);
    try {
      await axios.post(`${API}/spine/placements/${p.id}/accept`, {}, getAuthHeader());
      toast.success('Offer accepted — placement is now active');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept');
    } finally { setBusy(null); }
  };

  const handleDecline = (p) => setDeclineTarget(p);

  const confirmDecline = async (p, reason) => {
    setBusy(p.id);
    try {
      await axios.post(`${API}/spine/placements/${p.id}/decline`, { reason: reason || null }, getAuthHeader());
      toast.success('Offer declined — orchestrator is finding the next clinician');
      setDeclineTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not decline');
    } finally { setBusy(null); }
  };

  const handleCommitFirstVisit = (p) => setFirstVisitTarget(p);

  const confirmFirstVisit = async (p, atLocal, notes) => {
    setBusy(p.id);
    try {
      // datetime-local has no timezone — interpret as local + send ISO.
      const iso = new Date(atLocal).toISOString();
      await axios.post(
        `${API}/spine/placements/${p.id}/commit-first-visit`,
        { first_visit_at: iso, notes: notes || null },
        getAuthHeader(),
      );
      toast.success('First visit committed — discharge planner notified');
      setFirstVisitTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not commit first visit');
    } finally { setBusy(null); }
  };

  const counts = useMemo(() => {
    // We only have current bucket loaded — that's fine for the count badge.
    const obj = { offered: 0, accepted: 0, declined: 0, expired_no_response: 0 };
    obj[bucket] = placements.length;
    return obj;
  }, [placements, bucket]);

  return (
    <DashboardShell
      title="Your offers"
      subtitle="Real-time placement offers from the orchestrator — first to accept wins."
      navItems={[]}
      testId="spine-clinician-offers"
    >
      <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-cn-soft/50 border border-cn-border mb-6" data-testid="spine-offers-tabs">
        {STATUS_BUCKETS.map((b) => (
          <button
            key={b.v}
            onClick={() => setBucket(b.v)}
            data-testid={`spine-offers-tab-${b.v}`}
            className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all ${bucket === b.v ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}
          >
            <b.icon className="inline w-4 h-4 mr-1 -mt-0.5" />
            {b.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : placements.length === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-offers-empty">
          <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-cn-primary" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">No {STATUS_BUCKETS.find((b) => b.v === bucket)?.label.toLowerCase()} yet</h3>
          <p className="text-cn-text-muted max-w-md mx-auto">
            New placement offers will appear here automatically when the orchestrator routes a referral your way.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="spine-offers-grid">
          {placements.map((p) => (
            <OfferCard
              key={p.id}
              p={p}
              onAccept={handleAccept}
              onDecline={handleDecline}
              onCommitFirstVisit={handleCommitFirstVisit}
              busy={busy}
            />
          ))}
        </div>
      )}

      <DeclineModal
        open={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
        onConfirm={confirmDecline}
        placement={declineTarget}
        busy={busy === declineTarget?.id}
      />
      <FirstVisitModal
        open={!!firstVisitTarget}
        onClose={() => setFirstVisitTarget(null)}
        onConfirm={confirmFirstVisit}
        placement={firstVisitTarget}
        busy={busy === firstVisitTarget?.id}
      />
    </DashboardShell>
  );
}
